import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { completarUnidadeDoCadastro } from "../../../lib/unidadeVenda";
import { getAllProductsCached } from "../../../utils/cupomFormatters";
import { contaInicial, contaReaberta, desfazerNaConta, dividir, editarParteNaConta, itensDoPedido, registrar, rodada, situacao } from "../lib/contaPedido";
import { valorItem, valorSelecionado } from "../lib/divisao";
import { brl } from "../lib/formato";
import { apagarConta, gravarConta, lerConta } from "../lib/guardaLocal";
import { itensDaRodada } from "../lib/itensDoCupom";
import { visualForma } from "../lib/visualForma";
import { useCupomDaRodada } from "./useCupomDaRodada";

const SEM_DESCONTO = { tipo: "none", valor: 0, cupom: null };
const ONLINE = /pix_online|cartao_online/i;

const imagem = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `/api${url.startsWith("/") ? url : `/${url}`}`;
};

const numero = (texto) => parseFloat(String(texto).replace(",", "."));
const soma = (lista) => lista.reduce((s, p) => s + (Number(p.value) || 0), 0);

// Pedido reaberto em Vendas → Pedidos começa com os pagamentos de antes na lista
const contaDoZero = (pedido, base) => (pedido?.reaberto_em ? contaReaberta(pedido, base) : contaInicial(base.itens));

function useContaDoPedido(pedido) {
  const base = useMemo(() => (pedido ? itensDoPedido(pedido) : { itens: [], descontoPedido: 0 }), [pedido]);
  const [conta, setConta] = useState(() => contaDoZero(pedido, base));
  useEffect(() => {
    if (pedido) setConta(lerConta(pedido) || contaDoZero(pedido, base));
  }, [pedido, base]);
  // Quilo/litro (30/09/2026): o item gravado não guarda a unidade de venda —
  // completa pelo cadastro, para o produto a peso ser pago inteiro (lib/divisao).
  const [produtos, setProdutos] = useState(null);
  const temPedido = !!pedido;
  useEffect(() => {
    if (!temPedido || produtos) return undefined;
    let vivo = true;
    getAllProductsCached().then((l) => { if (vivo) setProdutos(Array.isArray(l) ? l : []); }).catch(() => {});
    return () => { vivo = false; };
  }, [temPedido, produtos]);
  useEffect(() => {
    if (!produtos) return;
    setConta((c) => {
      const items = completarUnidadeDoCadastro(c.items, produtos);
      return items === c.items ? c : { ...c, items };
    });
  }, [produtos, conta.items]);
  const atualizar = (nova) => { setConta(nova); if (pedido) gravarConta(pedido, nova); };
  return { conta, atualizar, descontoPedido: base.descontoPedido };
}

/**
 * Estado e ações do popup de fechamento para um pedido de retirada. Devolve o
 * mesmo objeto `f` que o Fechar mesa monta no pages/Mesas.js — as telas são as
 * mesmas. `onConcluir(conta)` grava o pedido e conclui; se falhar, a conta
 * quitada continua guardada e o botão vira "Concluir pedido" para tentar de novo.
 */
export function useFechamentoPedido({ pedido, formas, onConcluir, open, onOpenChange, abrirTroco }) {
  const { conta, atualizar, descontoPedido } = useContaDoPedido(pedido);
  const [desconto, setDesconto] = useState(SEM_DESCONTO);
  const [selecionada, setSelecionada] = useState(null);
  const [recebido, setRecebido] = useState("");
  const [override, setOverride] = useState("");
  // Edição de parte recusada no blur logo antes do Confirmar: não cobra o valor antigo
  const parteRecusada = useRef(0);
  // Concluindo: dois toques no Confirmar (01/11/2026) chamavam `onConcluir` duas
  // vezes antes de o servidor responder — Comandas fechava duas vezes (dois
  // POST /fechar), Mesas mandava dois PUT. A ref trava na hora; o estado
  // desabilita o botão.
  const [concluindo, setConcluindo] = useState(false);
  const concluindoRef = useRef(false);

  const ctx = { total: Number(pedido?.total) || 0, descontoPedido };
  const r = rodada(conta, { ...ctx, desconto });
  const sit = situacao(conta, ctx);
  const quitado = conta.log.length > 0 && sit.restante <= 0.009;
  const overrideNum = override !== "" ? numero(override) : NaN;
  const overrideAtivo = !conta.divisao && Number.isFinite(overrideNum) && overrideNum >= 0;
  const cobrar = overrideAtivo ? overrideNum : r.valor;
  // Nada a cobrar (desconto de 100% no pedido ou dado aqui): conclui com R$ 0,00
  // na forma escolhida. Antes o botão travava e a única saída era digitar um
  // valor, que virava o total do pedido (Point do Addad, #16597, 25/09/2026).
  const semValor = !overrideAtivo && !conta.divisao && r.valor <= 0.009;
  const ehDinheiro = /dinheiro/i.test(selecionada || "");
  const formasLocais = (formas || []).filter((m) => !ONLINE.test(m.code || m.id || "")).map((m) => ({ id: m.id || m.code, code: m.code || m.id, label: m.label }));

  const limparRodada = () => { setSelecionada(null); setRecebido(""); setOverride(""); };

  const concluir = async (nova) => {
    if (concluindoRef.current) return;
    concluindoRef.current = true;
    setConcluindo(true);
    try {
      await onConcluir(nova);
      apagarConta(pedido.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível concluir o pedido. O pagamento está guardado — tente de novo.");
    } finally {
      concluindoRef.current = false;
      setConcluindo(false);
    }
  };

  const confirmar = async () => {
    if (concluindoRef.current) return;
    if (quitado) { await concluir(conta); return; }
    if (!selecionada) { toast.error("Selecione uma forma de pagamento!"); return; }
    if (Date.now() - parteRecusada.current < 1500) return;
    if (ehDinheiro && !semValor && !(numero(recebido) >= cobrar - 0.005)) { toast.error("Informe o valor recebido."); return; }
    const pm = formasLocais.find((m) => m.code === selecionada);
    const troco = ehDinheiro && numero(recebido) > cobrar ? +(numero(recebido) - cobrar).toFixed(2) : null;
    const pagamento = { code: selecionada, label: pm?.label || selecionada, value: +cobrar.toFixed(2), troco_precisa: !!troco, troco_valor: troco, cash_received: ehDinheiro ? numero(recebido) : null };
    const antes = conta.descontos.length;
    const { conta: nova, quitou, parte } = registrar(conta, [pagamento], { ...ctx, desconto });
    atualizar(nova);
    limparRodada();
    if (nova.descontos.length !== antes) setDesconto(SEM_DESCONTO);
    if (quitou) { await concluir(nova); return; }
    const falta = situacao(nova, ctx).restante;
    toast.success(`${parte ? `Parte ${parte} paga` : `Pago ${brl(pagamento.value)}`} (${pagamento.label}) · falta ${brl(falta)}`);
  };

  const onDividir = (partes) => {
    const nova = dividir(conta, { ...ctx, desconto, partes });
    if (!nova) { toast.error("Não há valor para dividir."); return; }
    atualizar(nova);
    setDesconto(SEM_DESCONTO);
    setOverride("");
  };

  // Lixeira do resumo: aqui nada foi pro caixa ainda, é só voltar a conta
  const onApagar = async (id) => {
    const r = desfazerNaConta(conta, id);
    if (r.erro) { toast.error(r.erro); return; }
    atualizar(r.conta);
    limparRodada();
    toast.success(`Pagamento de ${brl(soma(r.entradas))} apagado.`);
  };

  const onEditarParte = (indice, valor) => {
    const r = editarParteNaConta(conta, indice, valor);
    if (r.erro) { parteRecusada.current = Date.now(); toast.error(r.erro); return false; }
    atualizar(r.conta);
    return true;
  };

  // Cupom: o servidor faz a conta com os produtos desta rodada e o canal do pedido
  // (pedido anotado pela CLEO conta como WhatsApp). Mudou a rodada, confere de novo.
  const moduloDoCupom = pedido?.origem === "cleo" ? "ChatBot" : (pedido?.modulo || "Delivery");
  const itensDoCupom = itensDaRodada(conta.items, { subtotal: r.subtotal });
  useCupomDaRodada({
    cupom: desconto.tipo === "coupon" ? desconto.cupom : null,
    subtotal: r.subtotal,
    itens: itensDoCupom,
    modulo: moduloDoCupom,
    clienteId: pedido?.cliente_id,
    clienteTelefone: pedido?.cliente_telefone,
    pausado: !!conta.divisao,
    aplicar: (cupom) => setDesconto((d) => (d.tipo === "coupon" ? { ...d, cupom: { ...d.cupom, ...cupom } } : d)),
    remover: (motivo) => { setDesconto(SEM_DESCONTO); toast.error(`Cupom saiu do pagamento: ${motivo}`, { duration: 6000 }); },
  });

  const mudarItem = (idx, mudanca) => atualizar({ ...conta, items: conta.items.map((it, i) => (i === idx ? { ...it, ...mudanca(it) } : it)) });
  const abertos = conta.items.filter((i) => !i._paid);
  const todosMarcados = abertos.length > 0 && abertos.every((i) => i._selected);
  const totalItens = conta.items.reduce((s, i) => s + valorItem(i), 0);

  return {
    open,
    onOpenChange,
    mesa: { cliente_nome: pedido?.cliente_nome },
    textos: { titulo: `Finalizar pedido ${pedido?.codigo || ""} — Pagamento`, produtos: "Produtos do pedido", inteira: "Pedido inteiro", doTodo: "do pedido inteiro" },
    getImageUrl: imagem,
    pmVisual: visualForma,
    produtos: {
      itens: conta.items,
      onToggle: (idx) => { if (!conta.divisao) mudarItem(idx, (it) => ({ _selected: !it._selected, _selQtd: null })); },
      onQtd: (idx, qtd) => mudarItem(idx, () => ({ _selected: true, _selQtd: qtd })),
      onToggleTodos: () => atualizar({ ...conta, items: conta.items.map((i) => (i._paid ? i : { ...i, _selected: !todosMarcados, _selQtd: null })) }),
      totalPago: sit.pago + conta.descPedUsado + conta.descontos.reduce((s, d) => s + (Number(d.valor) || 0), 0),
      totalMesa: totalItens,
      selecionadosQtd: abertos.filter((i) => i._selected).length,
      selecionadosTotal: conta.items.reduce((s, i) => s + valorSelecionado(i), 0),
    },
    divisao: {
      atual: conta.divisao,
      parte: conta.divisao ? { numero: conta.divisao.pagas + 1, total: conta.divisao.partes, valor: r.valor } : null,
      valorBase: conta.divisao ? 0 : r.valor,
      temDesconto: r.descPed + r.descManual > 0,
      onDividir,
      onCancelar: () => atualizar({ ...conta, divisao: null }),
      onEditarParte,
    },
    total: {
      subtotal: r.subtotal,
      desconto: r.descPed + r.descManual,
      calculado: r.valor,
      override,
      setOverride,
      overrideAtivo,
      overrideNum,
      pago: sit.pago,
      restante: sit.restante,
      log: conta.log,
      onApagar,
    },
    desconto: {
      tipo: desconto.tipo,
      valor: desconto.valor,
      cupom: desconto.cupom,
      montante: r.descManual,
      clienteId: pedido?.cliente_id,
      clienteTelefone: pedido?.cliente_telefone,
      itens: itensDoCupom,
      modulo: moduloDoCupom,
      onAplicar: ({ tipo, valor, cupom }) => setDesconto({ tipo, valor: valor || 0, cupom: cupom || null }),
      onRemover: () => setDesconto(SEM_DESCONTO),
    },
    pagamento: {
      formas: formasLocais,
      selecionada,
      setSelecionada,
      recebido,
      setRecebido,
      abrirPix: () => {},
      abrirTroco,
      total: cobrar,
      confirmar,
      podeConfirmar: !concluindo && (quitado || (!!selecionada && (cobrar > 0 || semValor) && !(ehDinheiro && !semValor && !(numero(recebido) >= cobrar - 0.005)))),
      rotuloBotao: quitado ? "Concluir pedido" : (semValor ? "Concluir sem cobrar (R$ 0,00)" : null),
    },
  };
}
